import React, { useMemo, useState } from 'react';
import api from '@/lib/api';
import { formatDateNL } from '@/lib/format';
import { CalendarClock, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Bulk-verlengen met verplichte bevestigingsstap. Gebruikt de bulk-route die per
 * medewerker dezelfde regels en een eigen auditregel toepast.
 */
export function BulkVerlengDialog({ employees, onClose, onDone }) {
  const [months, setMonths] = useState(12);
  const [datum, setDatum] = useState('');
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('kies'); // kies → bevestig → klaar
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const payload = useMemo(
    () => (datum ? { contract_einddatum: datum } : { months }),
    [datum, months],
  );
  const omschrijving = datum ? `nieuwe einddatum ${formatDateNL(datum)}` : `+${months} maanden`;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.patch('/employees/contract-verlengen-bulk', {
        ...payload, employee_ids: employees.map((e) => e.id), confirm: true,
        reason: reason.trim() || undefined,
      });
      setResult(data);
      setStep('klaar');
      onDone?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Bulk-verlengen mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="bulk-verleng-dialog">
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <CalendarClock size={16} /> Contracten verlengen
        </h3>
        <p className="mt-1 text-xs text-slate-500" data-testid="bulk-verleng-count">
          {employees.length} medewerker(s) geselecteerd
        </p>

        {step === 'kies' && (
          <>
            <div className="mt-4 flex gap-2">
              {[6, 12].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMonths(m); setDatum(''); }}
                  data-testid={`bulk-verleng-${m}m`}
                  className={`flex-1 rounded-md border px-3 py-1.5 text-sm ${
                    !datum && months === m ? 'border-brand-navy bg-brand-navy/5 font-medium' : 'border-slate-300 hover:bg-slate-50'}`}
                >
                  +{m} maanden
                </button>
              ))}
            </div>
            <label className="mt-4 block text-xs font-medium text-slate-600">Of één vaste datum</label>
            <input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              data-testid="bulk-verleng-datum"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
            <p className="mt-3 text-[11px] text-slate-500">
              Bij "+maanden" wordt gerekend vanaf de huidige einddatum, of vanaf vandaag als die in het verleden ligt.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-600">Reden/notitie (optioneel, geldt voor de hele set)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Bijv. cao-verlenging Q3"
              data-testid="bulk-verleng-reden"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={onClose} data-testid="bulk-verleng-cancel"
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
                Annuleren
              </button>
              <button type="button" onClick={() => setStep('bevestig')} data-testid="bulk-verleng-next"
                disabled={employees.length === 0}
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                Volgende
              </button>
            </div>
          </>
        )}

        {step === 'bevestig' && (
          <>
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="bulk-verleng-confirm">
              <p className="flex items-start gap-2 text-sm text-amber-900">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                Je verlengt <b>{employees.length}</b> contract(en) met <b>{omschrijving}</b>. Dit wordt per
                medewerker vastgelegd in het auditlog.
              </p>
              {reason && <p className="mt-1 text-xs text-amber-800" data-testid="bulk-verleng-reden-preview">Reden: {reason}</p>}
            </div>
            <ul className="mt-3 max-h-40 overflow-y-auto text-xs text-slate-600">
              {employees.map((e) => (
                <li key={e.id} className="py-0.5">
                  {e.name} <span className="text-slate-400">#{e.personeelsnummer}</span>
                  <span className="text-slate-400"> · nu {e.contract_einddatum ? formatDateNL(e.contract_einddatum) : '—'}</span>
                </li>
              ))}
            </ul>
            {error && <p className="mt-2 text-xs text-red-600" data-testid="bulk-verleng-error">{error}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setStep('kies')} data-testid="bulk-verleng-back"
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
                Terug
              </button>
              <button type="button" onClick={submit} disabled={busy} data-testid="bulk-verleng-confirm-btn"
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : 'Ja, verlengen'}
              </button>
            </div>
          </>
        )}

        {step === 'klaar' && result && (
          <>
            <div className="mt-4 rounded-lg border border-slate-200 p-3 text-sm" data-testid="bulk-verleng-result">
              <p><span className="font-semibold text-green-700">{result.gelukt} gelukt</span>
                {' · '}
                <span className={result.mislukt ? 'font-semibold text-red-700' : 'text-slate-500'}>
                  {result.mislukt} mislukt
                </span>
              </p>
              <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs">
                {result.results.map((r) => (
                  <li key={r.id} className={r.ok ? 'text-slate-600' : 'text-red-700'}>
                    {r.name || r.id}
                    {r.ok ? ` → ${formatDateNL(r.contract_einddatum)}` : ` — ${r.error}`}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} data-testid="bulk-verleng-close"
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">
                Sluiten
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
